import { useState, useEffect } from "react";
import { useOutletContext, useParams } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { type CellContext } from "@tanstack/react-table";
import { WaitlistEntries, WaitlistEntryNode, WaitlistEntry, Variables } from "./types";
import { WAITLIST_ENTRIES_QUERY } from "./queries";
import { toDateTimeFormat } from "@/lib/dateHelpers";
import { getStickyColumnClasses } from "@/lib/styleHelpers";
import usePagination from "@/hooks/usePagination";
import useTableRowMenu, { actionId } from "@/hooks/useTableRowMenu";
import Table, {
  TableControls,
  toTableControlActions,
  buildAvatarDetailsColumn,
  buildCopyTextColumn,
  buildActionColumn
} from "@/components/Table";
import EmptyState from "@/components/EmptyState";
import OverrideModal from "./_components/OverrideModal";
import { EventOutletContext } from "../_layout";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import PersonSideSheet from "@/features/person/components/PersonSideSheet";
import { formatPhoneNumber } from "@/lib/phoneHelpers";

export default function WaitlistPage() {
  const { eventSlug = "" } = useParams();
  const { Menu, setMenu } = useTableRowMenu();
  const { setErrorMessage, eventData } = useOutletContext<EventOutletContext>();
  const { isCompactLayout: isMobile } = useIsCompactLayout();

  const { paginationVariables, renderFooter, resetPagination } = usePagination();
  const [variables, setVariables] = useState<Variables>({ eventSlug });
  const [selectedPersonId, setSelectedPersonId] = useState<string | null>(null);
  const [isPersonSideSheetOpen, setIsPersonSideSheetOpen] = useState(false);
  const [selectedPgaId, setSelectedPgaId] = useState<string | null>(null);

  useEffect(() => {
    resetPagination();
    setVariables({
      eventSlug
    });
  }, [eventSlug, resetPagination]);

  const eventId = eventData?.id;
  const { hasWaitlist } = eventData || {};
  const showOverrideModal = !!(eventId && selectedPersonId);

  const { data, loading: queryLoading } = useQuery<WaitlistEntries>(WAITLIST_ENTRIES_QUERY, {
    variables: { ...variables, ...paginationVariables }
  });

  const waitlistEntriesData: WaitlistEntry[] = (data?.eventWaitlistEntries?.edges || []).map(
    ({ node: { age, createdAt, ...entry } }: WaitlistEntryNode) => ({
      ...entry,
      age,
      createdAt: createdAt ? toDateTimeFormat(createdAt, "LLL dd, yyyy h:mm a (ZZZZ)") : ""
    })
  );

  const handleCsvExport = () => {
    const headers = ["Name", "PGA ID", "Gender", "Age", "Email", "Phone", "Signup Date"];
    const rows = (data?.eventWaitlistEntries?.edges || []).map(({ node }) => [
      node.entryName || "",
      node.pgaId || "",
      node.gender || "",
      node.age?.toString() || "",
      node.email || "",
      node.phone || "",
      node.createdAt ? toDateTimeFormat(node.createdAt, "LLL dd, yyyy h:mm a (ZZZZ)") : ""
    ]);
    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const link = document.createElement("a");
    link.href = URL.createObjectURL(new Blob([csvContent], { type: "text/csv;charset=utf-8;" }));
    link.download = `waitlist-${eventSlug}-${Date.now()}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const columns = [
    ...buildAvatarDetailsColumn<WaitlistEntry>({
      isCompactLayout: isMobile,
      getTitle: (row) => row.entryName,
      getPicture: (row) => row.profilePhoto,
      header: "Name",
      size: 280,
      minSize: 200,
      id: "name"
    }).map((column) => ({
      ...column,
      ...(!isMobile && {
        meta: {
          ...column.meta,
          className: getStickyColumnClasses(!!waitlistEntriesData?.length)
        }
      })
    })),
    buildCopyTextColumn<WaitlistEntry>({
      accessorKey: "pgaId",
      header: "PGA ID",
      getText: (row) => row.pgaId,
      copyFieldLabel: "PGA ID",
      enableSorting: false
    }),
    {
      accessorKey: "gender",
      header: "Gender",
      enableSorting: false,
      meta: { headerClassName: "show-header-in-column" }
    },
    {
      accessorKey: "age",
      header: "Age",
      enableSorting: false,
      meta: { headerClassName: "show-header-in-column" }
    },
    { accessorKey: "email", header: "Email", enableSorting: false },
    {
      accessorKey: "phone",
      header: "Phone",
      enableSorting: false,
      size: 150,
      minSize: 130,
      cell: (cellProps: CellContext<WaitlistEntry, unknown>) => {
        const { phone } = cellProps.row.original;
        return <span className="whitespace-nowrap">{formatPhoneNumber(phone ?? null)}</span>;
      }
    },
    {
      accessorKey: "createdAt",
      header: "Signup",
      enableSorting: false,
      size: 220,
      minSize: 200,
      meta: { headerClassName: "show-header-in-column", className: "whitespace-nowrap" }
    },
    buildActionColumn<WaitlistEntry>({
      getActions: (row) => [
        {
          text: "Send Registration Override",
          onClick: () => {
            setSelectedPersonId(row.personId);
          }
        }
      ],
      onViewClick: (row) => {
        setSelectedPgaId(row.pgaId);
        setIsPersonSideSheetOpen(true);
      },
      viewButtonTitle: "View person details",
      actionId,
      setMenu
    })
  ];

  return (
    <>
      {showOverrideModal && (
        <OverrideModal
          eventId={eventId}
          personId={selectedPersonId}
          onClosed={() => {
            setSelectedPersonId(null);
          }}
          onError={(message: string) => {
            setErrorMessage(message);
          }}
        />
      )}
      <div data-testid="waitlist-page">
        <Menu />
        {hasWaitlist ? (
          <>
            <div className="pt-12" />
            <Table
              data={waitlistEntriesData}
              loading={queryLoading}
              onClickRow={(row) => {
                setSelectedPgaId(row.original.pgaId);
                setIsPersonSideSheetOpen(true);
              }}
              columns={columns}
              renderControls={(_table, { onSortClick } = {}) => (
                <TableControls
                  actions={
                    waitlistEntriesData.length > 0
                      ? toTableControlActions([
                          {
                            label: "Export Current List",
                            variant: "text",
                            icon: "download",
                            onClick: handleCsvExport
                          }
                        ])
                      : []
                  }
                  onSortClick={onSortClick}
                />
              )}
              renderNoDataEmptyState={() => (
                <EmptyState
                  title="No Waitlist Entries"
                  caption="This event does not have any waitlist entries"
                  iconName="person"
                />
              )}
              renderFooter={renderFooter(data?.eventWaitlistEntries, { pageCount: waitlistEntriesData.length })}
            />
          </>
        ) : (
          <EmptyState
            title="Waitlist Not Enabled"
            caption="This event disables registration when the attendance limit is reached."
            iconName="person"
            className="max-w-[600px]"
          />
        )}
      </div>
      <PersonSideSheet
        open={isPersonSideSheetOpen}
        onClose={() => {
          setIsPersonSideSheetOpen(false);
          setSelectedPgaId(null);
        }}
        pgaId={selectedPgaId}
      />
    </>
  );
}
